'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Discover', href: '/', key: 'nav-discover' },
  { label: 'Movies', href: '/movie-detail', key: 'nav-movies' },
  { label: 'Community', href: '/community-discussions', key: 'nav-community' },
  { label: 'Dashboard', href: '/user-dashboard', key: 'nav-dashboard' },
];

interface NavbarProps {
  activePath?: string;
}

export default function Navbar({ activePath = '/' }: NavbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 flex items-center h-16 gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={32} />
          <span className="font-sans text-xl font-800 text-foreground hidden sm:block tracking-tight">
            Cine<span className="text-gradient-red font-800">world</span>
          </span>
        </Link>

        {/* Search bar */}
        <div className={`flex-1 max-w-md mx-4 hidden md:flex items-center relative`}>
          <div className="relative w-full">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search movies, directors, genres..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-dark w-full pl-9 pr-4 py-2 text-sm"
            />
          </div>
        </div>

        {/* Nav links */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              className={`px-3 py-2 text-sm font-500 rounded-md transition-colors duration-150 ${
                activePath === link.href
                  ? 'text-accent nav-link-active' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex-1 lg:flex-none" />

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Mobile search */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="md:hidden btn-ghost p-2 rounded-md"
            aria-label="Search"
          >
            <Icon name="MagnifyingGlassIcon" size={18} className="text-muted-foreground" />
          </button>

          {/* Notifications */}
          <button className="relative btn-ghost p-2 rounded-md hidden sm:flex items-center" aria-label="Notifications">
            <Icon name="BellIcon" size={18} className="text-muted-foreground" />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
          </button>

          {/* Admin link */}
          <Link
            href="/admin-panel"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 text-muted-foreground hover:text-foreground btn-ghost rounded-md transition-colors"
          >
            <Icon name="Cog6ToothIcon" size={14} />
            Admin
          </Link>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-muted/50 transition-colors"
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-red-800 flex items-center justify-center text-xs font-700 text-white ring-2 ring-primary/30">
                MK
              </div>
              <span className="hidden md:block text-sm font-500 text-foreground">Marcus K.</span>
              <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground hidden md:block" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 glass-card-elevated rounded-xl border border-border shadow-modal scale-in overflow-hidden">
                <div className="px-3 py-2.5 border-b border-border">
                  <p className="text-sm font-600 text-foreground">Marcus Kim</p>
                  <p className="text-xs text-muted-foreground">marcus@cineworld.cloud</p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full status-approved font-500">Contributor</span>
                </div>
                <div className="py-1">
                  {[
                    { label: 'My Profile', icon: 'UserIcon', href: '/user-dashboard' },
                    { label: 'Watchlist', icon: 'BookmarkIcon', href: '/user-dashboard' },
                    { label: 'Watched History', icon: 'EyeIcon', href: '/user-dashboard' },
                    { label: 'Settings', icon: 'Cog6ToothIcon', href: '/user-dashboard' },
                  ].map((item) => (
                    <Link
                      key={`usermenu-${item.label}`}
                      href={item.href}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                    >
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                      {item.label}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-border py-1">
                  <button className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-primary hover:bg-primary/10 transition-colors">
                    <Icon name="ArrowRightOnRectangleIcon" size={14} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sign in button (unauthenticated state) — hidden when user menu visible */}
          <Link
            href="/sign-up-login"
            className="hidden btn-primary px-4 py-2 text-sm rounded-lg"
          >
            Sign In
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden btn-ghost p-2 rounded-md"
            aria-label="Menu"
          >
            <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} className="text-foreground" />
          </button>
        </div>
      </div>

      {/* Mobile search bar */}
      {searchOpen && (
        <div className="md:hidden px-4 pb-3 border-t border-border pt-3">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search movies..."
              className="input-dark w-full pl-9 pr-4 py-2 text-sm"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* Mobile nav menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border glass-card py-2">
          {navLinks.map((link) => (
            <Link
              key={`mobile-${link.key}`}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center px-4 py-3 text-sm font-500 transition-colors ${
                activePath === link.href ? 'text-accent bg-accent/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="border-t border-border mt-2 pt-2 px-4">
            <Link href="/sign-up-login" className="btn-primary w-full px-4 py-2.5 text-sm rounded-lg text-center block">
              Sign In
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}